import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';
import MathText from '../components/MathText';
import FolderPickerModal from '../components/FolderPickerModal';
import MathToolbar from '../components/MathToolbar';
import SvgToolbar from '../components/SvgToolbar';
import SEO from '../components/SEO';
import Modal from '../components/Modal';

const collapseDiagrams = (text) => {
  if (!text || typeof text !== 'string') return text;
  window.svgDiagrams = window.svgDiagrams || {};
  let collapsed = text;
  const svgRegex = /<svg[\s\S]*?<\/svg>/g;
  let match;
  
  while ((match = svgRegex.exec(collapsed)) !== null) {
    const svgHtml = match[0];
    let existingKey = null;
    for (const [key, val] of Object.entries(window.svgDiagrams)) {
      if (val === svgHtml) {
        existingKey = key;
        break;
      }
    }
    
    const key = existingKey || `[SVG-${Object.keys(window.svgDiagrams).length + 1}]`;
    window.svgDiagrams[key] = svgHtml;
    
    collapsed = collapsed.substring(0, match.index) + key + collapsed.substring(match.index + svgHtml.length);
    svgRegex.lastIndex = 0;
  }
  
  return collapsed;
};

const expandDiagrams = (text) => {
  if (!text || typeof text !== 'string') return text;
  let expanded = text;
  if (window.svgDiagrams) {
    for (const [key, val] of Object.entries(window.svgDiagrams)) {
      expanded = expanded.replace(new RegExp(key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g'), val);
    }
  }
  return expanded;
};

const isUrl = (str) => typeof str === 'string' && (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/'));

const emptyQuestion = () => ({
  text: '',
  type: 'normal',
  options: ['', '', '', ''],
  optionImages: ['', '', '', ''],
  correctAnswers: [],
  imageUrl: null,
  marks: 1,
  negativeMarks: 0,
  keyId: Math.random().toString(36).substring(2, 9),
});

const QuestionNumberInput = ({ idx, totalQuestions, onMove }) => {
  const [val, setVal] = useState(idx + 1);

  useEffect(() => {
    setVal(idx + 1);
  }, [idx]);

  return (
    <input
      type="number"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={(e) => {
        let newNum = parseInt(e.target.value, 10);
        if (isNaN(newNum) || newNum < 1) {
          setVal(idx + 1);
          return;
        }
        if (newNum > totalQuestions) {
          newNum = totalQuestions;
        }
        if (newNum - 1 !== idx) {
          onMove(idx, newNum - 1);
        } else {
          setVal(idx + 1);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      style={{
        background: 'var(--primary)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        textAlign: 'center',
        outline: 'none',
        padding: '0',
        boxShadow: 'var(--shadow-sm)',
        transition: 'transform 0.2s ease, background-color 0.2s ease',
        WebkitAppearance: 'none',
        MozAppearance: 'textfield',
        appearance: 'none'
      }}
      title="Enter new number to change order"
    />
  );
};

const ContentFormPage = ({ user, onBack, editItem, initialFolderId, onLogout, onGoToBrowse, onGoToDirectory, onCreatedItem, theme, onThemeChange }) => {
  // editItem: { type: 'document' | 'exam', item: {...} }
  const isEditing = !!editItem;
  const isEditingRoute = window.location.pathname.includes('/edit') || isEditing;
  const existingItem = editItem?.item;

  const [folders, setFolders] = useState([]);
  const [isSvgEditorOpen, setIsSvgEditorOpen] = useState(false);
  const getInitialForm = () => {
    if (!isEditing) return {
      title: '', type: 'document', folder: initialFolderId || '', visibility: 'public',
      releaseDate: new Date().toISOString().split('T')[0], questions: [], released: false,
      timeLimitMinutes: '', fileUrl: '', files: [], preferPdf: false
    };
    // Pre-fill from existing item
    const isExam = editItem.type === 'exam';
    return {
      title: existingItem.title || '',
      type: isExam ? 'exam_paper' : (existingItem.type || 'document'),
      folder: existingItem.folder?._id || existingItem.folder || '',
      visibility: existingItem.visibility || 'public',
      releaseDate: existingItem.releaseDate
        ? new Date(existingItem.releaseDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      questions: isExam ? (existingItem.questions || []).map(q => ({
        ...q,
        keyId: q._id || Math.random().toString(36).substring(2, 9),
        text: collapseDiagrams(q.text),
        options: (q.options || []).map(o => collapseDiagrams(o)),
        optionImages: Array.isArray(q.optionImages) && q.optionImages.length > 0 
          ? [...q.optionImages] 
          : (q.options || []).map(() => ''),
        correctAnswers: (q.correctAnswers || []).map(a => collapseDiagrams(a))
      })) : [],
      released: !!existingItem.released,
      timeLimitMinutes: existingItem.timeLimitMinutes ?? '',
      fileUrl: existingItem.fileUrl || '',
      files: Array.isArray(existingItem.files) && existingItem.files.length > 0 ? [...existingItem.files] : (existingItem.fileUrl ? [{url: existingItem.fileUrl, name: existingItem.title || 'Paper', visibility: 'public'}] : []),
      preferPdf: !!existingItem.preferPdf
    };
  };

  const getCoreDetails = (f) => ({
    title: f.title,
    type: f.type,
    folder: f.folder,
    visibility: f.visibility,
    releaseDate: f.releaseDate,
    released: f.released,
    timeLimitMinutes: f.timeLimitMinutes,
    preferPdf: f.preferPdf
  });

  const [form, setForm] = useState(getInitialForm);
  const [lastSavedCore, setLastSavedCore] = useState(() => getCoreDetails(getInitialForm()));
  const [question, setQuestion] = useState(emptyQuestion());
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editQuestionsData, setEditQuestionsData] = useState({});
  const updateEditQuestionData = (idx, updater) => {
    setEditQuestionsData(prev => {
      const curr = prev[idx];
      const next = typeof updater === 'function' ? updater(curr) : updater;
      return {
        ...prev,
        [idx]: next
      };
    });
  };
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [pageError, setPageError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [folderError, setFolderError] = useState('');
  const [fileError, setFileError] = useState('');
  const [fileSuccess, setFileSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [timeLimitError, setTimeLimitError] = useState('');
  const [lastFocusedId, setLastFocusedId] = useState(null);
  const [showCoreDetails, setShowCoreDetails] = useState(!isEditingRoute);
  const [showPicker, setShowPicker] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState(null);
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);
  const [draftItemId, setDraftItemId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [questionsHistory, setQuestionsHistory] = useState([]);

  useEffect(() => {
    const handleUndo = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        const activeTag = document.activeElement?.tagName?.toLowerCase();
        if (activeTag === 'input' && (document.activeElement.type === 'text' || document.activeElement.type === 'number')) return;
        if (activeTag === 'textarea') return;

        e.preventDefault();
        setQuestionsHistory(prevHistory => {
          if (prevHistory.length === 0) return prevHistory;
          const nextHistory = [...prevHistory];
          const previousQuestions = nextHistory.pop();
          
          setForm(prev => ({ ...prev, questions: previousQuestions }));
          
          const serverItemId = isEditing ? existingItem?._id : draftItemId;
          if (serverItemId) {
            const payload = {
              title: form.title,
              type: form.type,
              folder: form.folder,
              visibility: form.visibility,
              releaseDate: form.releaseDate,
              released: form.released,
              preferPdf: form.preferPdf,
              timeLimitMinutes: form.timeLimitMinutes !== '' ? Number(form.timeLimitMinutes) : undefined,
            };
            const expandedQuestions = previousQuestions.map(q => ({
              ...q,
              text: expandDiagrams(q.text),
              options: (q.options || []).map(o => expandDiagrams(o)),
              correctAnswers: (q.correctAnswers || []).map(a => expandDiagrams(a))
            }));
            api.put(`/exams/${serverItemId}`, { ...payload, questions: expandedQuestions })
              .then(() => setStatus('Question order reverted successfully!'))
              .catch(err => alert(err.response?.data?.error || 'Failed to sync reverted order on server'));
          }
          return nextHistory;
        });
      }
    };
    window.addEventListener('keydown', handleUndo);
    return () => window.removeEventListener('keydown', handleUndo);
  }, [questionsHistory, form, isEditing, existingItem, draftItemId]);

  // Question navigation map states and drag variables
  const [showMap, setShowMap] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(window.innerWidth <= 1024);
  const [panelHeight, setPanelHeight] = useState(window.innerHeight * 0.5);

  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const currentHeightRef = useRef(window.innerHeight * 0.5);
  const dragStartTimeRef = useRef(0);

  const handleDragMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    const deltaY = clientY - dragStartYRef.current;
    const newHeight = Math.max(100, Math.min(window.innerHeight, dragStartHeightRef.current - deltaY));
    setPanelHeight(newHeight);
    currentHeightRef.current = newHeight;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    document.body.style.userSelect = '';

    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    window.removeEventListener('touchmove', handleDragMove);
    window.removeEventListener('touchend', handleDragEnd);

    const dragDuration = Date.now() - dragStartTimeRef.current;
    const distanceMovedDown = dragStartHeightRef.current - currentHeightRef.current;
    
    // Calculate downward drag velocity (pixels per millisecond)
    const velocity = dragDuration > 0 ? distanceMovedDown / dragDuration : 0;

    // Swift downward swipe (velocity > 0.5px/ms and dragged down > 30px) closes it
    const isSwipeDown = velocity > 0.5 && distanceMovedDown > 30;

    if (isSwipeDown || currentHeightRef.current < window.innerHeight * 0.25) {
      setShowMap(false);
      setPanelHeight(window.innerHeight * 0.5);
      currentHeightRef.current = window.innerHeight * 0.5;
    }
  }, [handleDragMove]);

  const handleDragStart = (e) => {
    isDraggingRef.current = true;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    dragStartYRef.current = clientY;
    dragStartHeightRef.current = panelHeight;
    dragStartTimeRef.current = Date.now();
    document.body.style.userSelect = 'none';

    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 720);
      setIsMobileOrTablet(window.innerWidth <= 1024);
      if (showMap && !isDraggingRef.current) {
        setPanelHeight(window.innerHeight * 0.5);
        currentHeightRef.current = window.innerHeight * 0.5;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showMap]);

  useEffect(() => {
    if (showMap) {
      setPanelHeight(window.innerHeight * 0.5);
      currentHeightRef.current = window.innerHeight * 0.5;
    }
  }, [showMap]);

  const panelStyle = isMobileOrTablet ? { height: `${panelHeight}px` } : {};
  
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Track unsaved changes for browser beforeunload
  const isCoreDirty = JSON.stringify({
    title: form.title, type: form.type, folder: form.folder, visibility: form.visibility,
    releaseDate: form.releaseDate, released: form.released, timeLimitMinutes: form.timeLimitMinutes, preferPdf: form.preferPdf
  }) !== JSON.stringify(lastSavedCore);
  
  const hasUnsavedChanges = isCoreDirty || pendingFiles.length > 0;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const questionErrorRef = useRef(null);

  const showQuestionError = (message) => {
    setError(message);
    setTimeout(() => {
      if (typeof questionErrorRef.current?.scrollIntoView === 'function') {
        questionErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      questionErrorRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const handleFileDelete = async () => {
    if (deleteConfirmIdx === null || !form.files || !form.files[deleteConfirmIdx]) return;
    const fileUrlToDelete = form.files[deleteConfirmIdx].url;

    setDeleting(true);
    setFileError('');
    setFileSuccess('');
    
    try {
      await api.delete('/upload/pdf', { data: { fileUrl: fileUrlToDelete } });
      
      const updatedFiles = (form.files || []).filter((_, i) => i !== deleteConfirmIdx);
      
      setForm(prev => ({ 
        ...prev, 
        files: updatedFiles, 
        fileUrl: updatedFiles.length === 0 ? '' : prev.fileUrl, 
        preferPdf: updatedFiles.length === 0 ? false : prev.preferPdf 
      }));
      
      // Sync with DB immediately
      if (isEditing) {
        const id = existingItem._id;
        if (editItem.type === 'exam') {
          await api.put(`/exams/${id}`, { files: updatedFiles, preferPdf: updatedFiles.length === 0 ? false : form.preferPdf });
        } else {
          await api.put(`/documents/${id}`, { files: updatedFiles });
        }
      } else if (draftItemId) {
        if (form.type === 'exam_paper') {
          await api.put(`/exams/${draftItemId}`, { files: updatedFiles, preferPdf: updatedFiles.length === 0 ? false : form.preferPdf });
        } else {
          await api.put(`/documents/${draftItemId}`, { files: updatedFiles });
        }
      }

      setFileSuccess('File deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      setFileError(err.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(false);
      setDeleteConfirmIdx(null);
    }
  };

  const toggleFileVisibility = async (idx) => {
    if (!form.files || !form.files[idx]) return;
    const currentVis = form.files[idx].visibility || 'public';
    const newVis = currentVis === 'public' ? 'private' : 'public';
    
    let updatedFiles = [...form.files];
    updatedFiles[idx] = { ...updatedFiles[idx], visibility: newVis };
    
    setForm(prev => ({ ...prev, files: updatedFiles }));
    
    const currentId = isEditing ? existingItem._id : draftItemId;
    if (currentId) {
      const isExam = isEditing ? editItem.type === 'exam' : form.type === 'exam_paper';
      const endpoint = isExam ? `/exams/${currentId}` : `/documents/${currentId}`;
      try {
        await api.put(endpoint, { files: updatedFiles, preferPdf: form.preferPdf });
        setFileSuccess(`File is now ${newVis}`);
        setTimeout(() => setFileSuccess(''), 3000);
      } catch (err) {
        setFileError('Failed to update file visibility');
        setTimeout(() => setFileError(''), 3000);
      }
    }
  };

  const cleanupCancelledImages = async (cancelledQuestion, originalQuestion = null) => {
    const urlsToDelete = [];
    
    if (cancelledQuestion.imageUrl && (!originalQuestion || originalQuestion.imageUrl !== cancelledQuestion.imageUrl)) {
      urlsToDelete.push(cancelledQuestion.imageUrl);
    }
    
    if (cancelledQuestion.optionImages && cancelledQuestion.optionImages.length > 0) {
      cancelledQuestion.optionImages.forEach((img, idx) => {
        if (img && (!originalQuestion || !originalQuestion.optionImages || originalQuestion.optionImages[idx] !== img)) {
          urlsToDelete.push(img);
        }
      });
    }

    if (urlsToDelete.length > 0) {
      for (const url of urlsToDelete) {
        try {
          await api.delete('/upload/image', { data: { fileUrl: url } });
        } catch (err) {
          console.error('Failed to clean up cancelled image:', url, err);
        }
      }
    }
  };

  const handleBack = async () => {
    const isInitialCreate = !isEditing && showCoreDetails;
    const hasUnsavedChanges = isInitialCreate && !draftItemId && (form.title.trim() !== '' || pendingFiles.length > 0);

    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return;
      }
    }
    
    // Cleanup main Add Question images
    await cleanupCancelledImages(question, null);
    // Cleanup other editing questions
    for (const [key, val] of Object.entries(editQuestionsData)) {
      const qIdx = Number(key);
      const orig = form.questions[qIdx];
      await cleanupCancelledImages(val, orig);
    }
    onBack?.();
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    setFileError('');
    setFileSuccess('');

    const validFiles = selectedFiles.filter(f => f.type === 'application/pdf');
    if (validFiles.length !== selectedFiles.length) {
      setFileError('Only PDF files are allowed');
    }

    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
    }
    
    // Clear input so same file can be selected again if removed
    if (e.target) e.target.value = '';
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const [uploadingQuestionImage, setUploadingQuestionImage] = useState(false);
  const [uploadingOptionImageIdx, setUploadingOptionImageIdx] = useState(null);

  const uploadQuestionImageFile = async (file, idx = null) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    const serverItemId = isEditing ? existingItem._id : draftItemId;
    if (!serverItemId) {
      setError('Please save basic info first before uploading question images.');
      return;
    }

    const formData = new FormData();
    formData.append('type', 'question_image');
    formData.append('folder', form.folder || '');
    formData.append('examId', serverItemId);
    formData.append('image', file);

    setUploadingQuestionImage(true);
    setError('');

    try {
      const res = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
      setQ(prev => ({ ...prev, imageUrl: res.data.fileUrl }));
    } catch (err) {
      setError(err.response?.data?.error || 'Image upload failed');
    } finally {
      setUploadingQuestionImage(false);
    }
  };

  const handleQuestionImageUpload = async (e, idx = null) => {
    const file = e.target.files[0];
    await uploadQuestionImageFile(file, idx);
  };

  const handleQuestionImageDelete = async (idx = null) => {
    const currQ = idx !== null ? editQuestionsData[idx] : question;
    if (!currQ?.imageUrl) return;
    try {
      await api.delete('/upload/image', { data: { fileUrl: currQ.imageUrl } });
      const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
      setQ(prev => ({ ...prev, imageUrl: null }));
    } catch (err) {
      setError('Failed to delete image');
    }
  };

  const uploadOptionImageFile = async (optIdx, file, idx = null) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    const serverItemId = isEditing ? existingItem._id : draftItemId;
    if (!serverItemId) {
      setError('Please save basic info first before uploading option images.');
      return;
    }

    const formData = new FormData();
    formData.append('type', 'option_image');
    formData.append('folder', form.folder || '');
    formData.append('examId', serverItemId);
    formData.append('image', file);

    setUploadingOptionImageIdx(optIdx);
    setError('');

    try {
      const res = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
      setQ(prev => {
        const optionImages = [...(prev.optionImages || [])];
        while (optionImages.length < prev.options.length) {
          optionImages.push('');
        }
        optionImages[optIdx] = res.data.fileUrl;
        return { ...prev, optionImages };
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Image upload failed');
    } finally {
      setUploadingOptionImageIdx(null);
    }
  };

  const handleOptionImageUpload = async (optIdx, e, idx = null) => {
    const file = e.target.files[0];
    await uploadOptionImageFile(optIdx, file, idx);
  };

  // Handle Ctrl+V paste of images into question/option fields
  const handlePasteImage = (e, target, idx = null, optIdx = null) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData || !clipboardData.items) return;

    let imageFile = null;
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];
      if (item.type.startsWith('image/')) {
        imageFile = item.getAsFile();
        break;
      }
    }

    if (!imageFile) return; // No image in clipboard, let default paste happen

    e.preventDefault(); // Prevent pasting image data as text

    if (target === 'question') {
      uploadQuestionImageFile(imageFile, idx);
    } else if (target === 'option' && optIdx !== null) {
      uploadOptionImageFile(optIdx, imageFile, idx);
    }
  };

  const handleOptionImageDelete = async (optIdx, idx = null) => {
    const currQ = idx !== null ? editQuestionsData[idx] : question;
    const url = currQ?.optionImages?.[optIdx];
    if (!url) return;
    try {
      await api.delete('/upload/image', { data: { fileUrl: url } });
      const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
      setQ(prev => {
        const optionImages = [...(prev.optionImages || [])];
        optionImages[optIdx] = '';
        return { ...prev, optionImages };
      });
    } catch (err) {
      setError('Failed to delete image');
    }
  };

  useEffect(() => {
    api.get('/folders')
      .then(r => setFolders(r.data))
      .catch(err => setPageError(err.response?.data?.error || 'Unable to load folders'));
  }, []);

  useEffect(() => {
    setQuestion(emptyQuestion());
    setEditingQuestionIndex(null);
    setEditQuestionsData({});
    setShowPreview(false);
    setIsSvgEditorOpen(false);
    setDraftItemId(null);
    setShowCoreDetails(!isEditingRoute);
    setTitleError('');
    setFolderError('');
    setFileError('');
    setFileSuccess('');
    setSubmitError('');
    setSubmitSuccess('');
    setTimeLimitError('');
    setForm(() => {
      if (!isEditing) {
        return {
          title: '',
          type: 'document',
          folder: initialFolderId || '',
          visibility: 'public',
          releaseDate: new Date().toISOString().split('T')[0],
          questions: [],
          released: false,
          timeLimitMinutes: '',
          fileUrl: '',
          files: [],
          preferPdf: false
        };
      }
      const isExam = editItem.type === 'exam';
      return {
        title: existingItem.title || '',
        type: isExam ? 'exam_paper' : (existingItem.type || 'document'),
        folder: existingItem.folder?._id || existingItem.folder || '',
        visibility: existingItem.visibility || 'public',
        releaseDate: existingItem.releaseDate
          ? new Date(existingItem.releaseDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        questions: isExam ? (existingItem.questions || []).map(q => ({
          ...q,
          keyId: q._id || Math.random().toString(36).substring(2, 9),
          text: collapseDiagrams(q.text),
          options: (q.options || []).map(o => collapseDiagrams(o)),
          optionImages: Array.isArray(q.optionImages) && q.optionImages.length > 0 
            ? [...q.optionImages] 
            : (q.options || []).map(() => ''),
          correctAnswers: (q.correctAnswers || []).map(a => collapseDiagrams(a))
        })) : [],
        released: !!existingItem.released,
        timeLimitMinutes: existingItem.timeLimitMinutes ?? '',
        fileUrl: existingItem.fileUrl || '',
        files: Array.isArray(existingItem.files) && existingItem.files.length > 0 ? [...existingItem.files] : (existingItem.fileUrl ? [{url: existingItem.fileUrl, name: existingItem.title || 'Paper'}] : []),
        preferPdf: !!existingItem.preferPdf
      };
    });
  }, [editItem, initialFolderId, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleOptionChange = (optIdx, value, idx = null) => {
    const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
    setQ(prev => {
      const options = [...(prev.options || [])];
      const optionImages = [...(prev.optionImages || [])];
      const oldText = options[optIdx];
      const oldImg = optionImages[optIdx];
      const oldIdentifier = oldText?.trim() || oldImg;

      options[optIdx] = value;
      const newIdentifier = value?.trim() || oldImg;

      let correctAnswers = [...(prev.correctAnswers || [])];
      if (oldIdentifier && correctAnswers.includes(oldIdentifier)) {
        correctAnswers = correctAnswers.filter(a => a !== oldIdentifier);
        if (newIdentifier) {
          correctAnswers.push(newIdentifier);
        }
      }
      return { ...prev, options, correctAnswers };
    });
  };

  const toggleCorrectAnswer = (optIdx, idx = null) => {
    const currQ = idx !== null ? editQuestionsData[idx] : question;
    const optText = currQ?.options[optIdx];
    const optImg = currQ?.optionImages?.[optIdx];
    const identifier = optText?.trim() || optImg;

    if (!identifier) {
      setError('Please type option text or upload an image before marking it as correct.');
      return;
    }
    const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
    setQ(prev => {
      const already = prev.correctAnswers.includes(identifier);
      return {
        ...prev,
        correctAnswers: already
          ? prev.correctAnswers.filter(a => a !== identifier)
          : [...prev.correctAnswers, identifier],
      };
    });
  };

  const addOption = (idx = null) => {
    const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
    setQ(prev => ({ 
      ...prev, 
      options: [...(prev.options || []), ''], 
      optionImages: [...(prev.optionImages || []), ''] 
    }));
  };

  const removeOption = (optIdx, idx = null) => {
    const currQ = idx !== null ? editQuestionsData[idx] : question;
    const removedText = currQ?.options[optIdx];
    const removedImg = currQ?.optionImages?.[optIdx];
    const removedIdentifier = removedText?.trim() || removedImg;
    const setQ = idx !== null ? (updater) => updateEditQuestionData(idx, updater) : setQuestion;
    setQ(prev => ({
      ...prev,
      options: (prev.options || []).filter((_, i) => i !== optIdx),
      optionImages: (prev.optionImages || []).filter((_, i) => i !== optIdx),
      correctAnswers: (prev.correctAnswers || []).filter(a => a !== removedIdentifier),
    }));
  };

  const saveQuestion = async (idx = null) => {
    setError('');
    setStatus('');
    const targetQuestion = idx !== null ? editQuestionsData[idx] : question;
    if (!targetQuestion) return;

    if (!targetQuestion.text?.trim() && !targetQuestion.imageUrl) { showQuestionError('Question text or image is required'); return; }
    if (targetQuestion.type === 'mcq') {
      const filled = targetQuestion.options.filter((o, oIdx) => o?.trim() || targetQuestion.optionImages?.[oIdx]);
      if (filled.length < 2) { showQuestionError('MCQ needs at least 2 options'); return; }
      if (targetQuestion.correctAnswers.length === 0) { showQuestionError('Select at least one correct answer'); return; }
    }

    let newQuestions;
    if (idx !== null) {
      newQuestions = [...form.questions];
      newQuestions[idx] = targetQuestion;
    } else {
      newQuestions = [...form.questions, targetQuestion];
    }

    const serverItemId = isEditing ? existingItem._id : draftItemId;
    const hasServerItem = isEditing || !!draftItemId;

    if (!hasServerItem) {
      showQuestionError('Save basic info before adding questions.');
      return;
    }

    try {
      const payload = {
        title: form.title,
        type: form.type,
        folder: form.folder,
        visibility: form.visibility,
        releaseDate: form.releaseDate,
        released: form.released,
        preferPdf: form.preferPdf,
        timeLimitMinutes: form.timeLimitMinutes !== '' ? Number(form.timeLimitMinutes) : undefined,
      };
      const expandedQuestions = newQuestions.map(q => ({
        ...q,
        text: expandDiagrams(q.text),
        options: (q.options || []).map(o => expandDiagrams(o)),
        correctAnswers: (q.correctAnswers || []).map(a => expandDiagrams(a))
      }));
      const response = await api.put(`/exams/${serverItemId}`, { ...payload, questions: expandedQuestions });
      setStatus('Question saved successfully!');

      const savedQuestions = (response.data.questions || []).map(savedQ => ({
        ...savedQ,
        text: collapseDiagrams(savedQ.text),
        options: (savedQ.options || []).map(o => collapseDiagrams(o)),
        optionImages: Array.isArray(savedQ.optionImages) && savedQ.optionImages.length > 0 
          ? [...savedQ.optionImages] 
          : (savedQ.options || []).map(() => ''),
        correctAnswers: (savedQ.correctAnswers || []).map(a => collapseDiagrams(a))
      }));
      setForm(prev => ({ ...prev, questions: savedQuestions }));
      
      // Close the specific editor
      if (idx !== null) {
        setEditQuestionsData(prev => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });
      } else {
        setQuestion(emptyQuestion());
      }
    } catch (err) {
      showQuestionError(err.response?.data?.error || 'Failed to save question to server');
      return;
    }
  };

  const editQuestion = (idx) => {
    const q = form.questions[idx];
    const initialData = {
      ...emptyQuestion(),
      ...q,
      options: Array.isArray(q.options) && q.options.length > 0 ? [...q.options] : ['', '', '', ''],
      optionImages: Array.isArray(q.optionImages) && q.optionImages.length > 0 
        ? [...q.optionImages] 
        : (Array.isArray(q.options) ? q.options.map(() => '') : ['', '', '', '']),
      correctAnswers: Array.isArray(q.correctAnswers) ? [...q.correctAnswers] : []
    };
    setEditQuestionsData(prev => ({
      ...prev,
      [idx]: initialData
    }));
  };

  const removeQuestion = async (idx) => {
    const newQuestions = form.questions.filter((_, i) => i !== idx);

    const serverItemId = isEditing ? existingItem._id : draftItemId;
    const hasServerItem = isEditing || !!draftItemId;

    if (hasServerItem) {
      try {
        const payload = {
          title: form.title,
          type: form.type,
          folder: form.folder,
          visibility: form.visibility,
          releaseDate: form.releaseDate,
          released: form.released,
          preferPdf: form.preferPdf,
          timeLimitMinutes: form.timeLimitMinutes !== '' ? Number(form.timeLimitMinutes) : undefined,
        };
        const expandedQuestions = newQuestions.map(q => ({
          ...q,
          text: expandDiagrams(q.text),
          options: (q.options || []).map(o => expandDiagrams(o)),
          correctAnswers: (q.correctAnswers || []).map(a => expandDiagrams(a))
        }));
        await api.put(`/exams/${serverItemId}`, { ...payload, questions: expandedQuestions });
        setStatus('Question removed successfully!');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to remove question from server');
        return;
      }
    }

    setForm(prev => ({ ...prev, questions: newQuestions }));
    setEditQuestionsData(prev => {
      const next = {};
      for (const [key, val] of Object.entries(prev)) {
        const k = Number(key);
        if (k === idx) {
          continue;
        } else if (k > idx) {
          next[k - 1] = val;
        } else {
          next[k] = val;
        }
      }
      return next;
    });
  };

  const handleMoveQuestion = async (sourceIdx, targetIdx) => {
    if (sourceIdx === targetIdx) return;

    setQuestionsHistory(prev => [...prev, form.questions]);

    const originalQuestions = form.questions;
    const updatedQuestions = [...form.questions];
    const [movedQuestion] = updatedQuestions.splice(sourceIdx, 1);
    updatedQuestions.splice(targetIdx, 0, movedQuestion);

    // Optimistic UI update
    setForm(prev => ({ ...prev, questions: updatedQuestions }));

    const serverItemId = isEditing ? existingItem?._id : draftItemId;
    if (serverItemId) {
      try {
        const payload = {
          title: form.title,
          type: form.type,
          folder: form.folder,
          visibility: form.visibility,
          releaseDate: form.releaseDate,
          released: form.released,
          preferPdf: form.preferPdf,
          timeLimitMinutes: form.timeLimitMinutes !== '' ? Number(form.timeLimitMinutes) : undefined,
        };
        const expandedQuestions = updatedQuestions.map(q => ({
          ...q,
          text: expandDiagrams(q.text),
          options: (q.options || []).map(o => expandDiagrams(o)),
          correctAnswers: (q.correctAnswers || []).map(a => expandDiagrams(a))
        }));
        await api.put(`/exams/${serverItemId}`, { ...payload, questions: expandedQuestions });
        setStatus('Question order updated successfully!');
      } catch (err) {
        setForm(prev => ({ ...prev, questions: originalQuestions }));
        alert(err.response?.data?.error || 'Failed to update question order on server');
      }
    }
  };

  const saveCoreDetails = async () => {
    setTitleError('');
    setFolderError('');
    setFileError('');
    setSubmitError('');
    setSubmitSuccess('');
    setTimeLimitError('');

    let hasValidationError = false;

    if (!form.title?.trim()) {
      setTitleError('Title is required');
      hasValidationError = true;
    }
    if (form.folder === null || form.folder === undefined) {
      setFolderError('Folder is required');
      hasValidationError = true;
    }

    if (form.type !== 'exam_paper' && (!form.files || form.files.length === 0) && pendingFiles.length === 0) {
      setFileError('Please upload at least one PDF file for the document before saving.');
      hasValidationError = true;
    }

    if (form.type === 'exam_paper' && form.timeLimitMinutes !== '') {
      const val = Number(form.timeLimitMinutes);
      if (isNaN(val) || val < 1 || val > 360) {
        setTimeLimitError('Time limit must be between 1 and 360 minutes');
        hasValidationError = true;
      }
    }

    if (hasValidationError) {
      return;
    }

    const payload = {
      title: form.title,
      type: form.type,
      folder: form.folder,
      visibility: form.visibility,
      releaseDate: form.releaseDate,
      released: form.released,
      preferPdf: form.preferPdf,
      timeLimitMinutes: form.timeLimitMinutes !== '' ? Number(form.timeLimitMinutes) : undefined,
      files: form.files,
      fileUrl: form.files && form.files.length > 0 ? form.files[0].url : undefined
    };

    try {
      let currentId = isEditing ? existingItem._id : draftItemId;

      // 1. Create or Update Core Details
      if (currentId) {
        const isOriginallyExam = isEditing
          ? editItem.type === 'exam'
          : form.type === 'exam_paper';
        if (isOriginallyExam) {
          const res = await api.put(`/exams/${currentId}`, payload);
          if (res.data && res.data.files !== undefined) {
            setForm(prev => ({ 
              ...prev, 
              files: res.data.files, 
              fileUrl: res.data.fileUrl || '',
              preferPdf: res.data.preferPdf || false
            }));
          }
        } else {
          const res = await api.put(`/documents/${currentId}`, { ...payload, content: form.content });
          if (res.data && res.data.files !== undefined) {
            setForm(prev => ({ 
              ...prev, 
              files: res.data.files, 
              fileUrl: res.data.fileUrl || '' 
            }));
          }
        }
      } else {
        if (form.type === 'exam_paper') {
          const response = await api.post('/exams', { ...payload, questions: form.questions });
          currentId = response.data._id;
          setDraftItemId(currentId);
          if (response.data && response.data.files !== undefined) {
            setForm(prev => ({ 
              ...prev, 
              files: response.data.files, 
              fileUrl: response.data.fileUrl || '',
              preferPdf: response.data.preferPdf || false
            }));
          }
        } else {
          const response = await api.post('/documents', { ...payload, type: form.type });
          currentId = response.data._id;
          setDraftItemId(currentId);
          if (response.data && response.data.files !== undefined) {
            setForm(prev => ({ 
              ...prev, 
              files: response.data.files, 
              fileUrl: response.data.fileUrl || '' 
            }));
          }
        }
      }

      // 2. Process Pending File Uploads
      if (pendingFiles.length > 0) {
        setUploading(true);
        setSubmitSuccess('Saving details... starting file uploads.');
        const newSuccessFiles = [];
        const remainingPending = [];

        for (const file of pendingFiles) {
          if (!isMounted.current) break;

          const formData = new FormData();
          formData.append('type', form.type);
          formData.append('folder', form.folder || '');
          formData.append('examId', currentId);

          const getFolderPath = (folderId, allFolders) => {
            if (!folderId) return '';
            const current = allFolders.find(f => f._id === folderId);
            if (!current) return '';
            const parentPath = getFolderPath(current.parent, allFolders);
            return parentPath ? `${parentPath}/${current.title}` : current.title;
          };
          const resolvedFolderPath = getFolderPath(form.folder, folders);
          if (resolvedFolderPath) {
            formData.append('folderPath', resolvedFolderPath);
          }

          formData.append('pdf', file);

          try {
            const res = await api.post('/upload/pdf', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            newSuccessFiles.push({ url: res.data.fileUrl, name: file.name.replace(/\.pdf$/i, ''), visibility: 'public' });
          } catch (err) {
            remainingPending.push(file);
            if (isMounted.current) setFileError(`Upload failed for ${file.name}. You can try again.`);
          }
        }

        setPendingFiles(remainingPending);

        // 3. Sync Successful Uploads to Database
        if (newSuccessFiles.length > 0) {
          const allFiles = [...(form.files || []), ...newSuccessFiles];
          const syncPayload = { files: allFiles };
          
          const isOriginallyExam = isEditing
            ? editItem.type === 'exam'
            : form.type === 'exam_paper';
            
          let res;
          if (isOriginallyExam) {
            res = await api.put(`/exams/${currentId}`, syncPayload);
          } else {
            res = await api.put(`/documents/${currentId}`, syncPayload);
          }
          
          if (res && res.data && res.data.files !== undefined) {
            setForm(prev => ({ 
              ...prev, 
              files: res.data.files, 
              fileUrl: res.data.fileUrl || '' 
            }));
          } else {
            setForm(prev => ({ ...prev, files: allFiles, fileUrl: allFiles[0]?.url || prev.fileUrl }));
          }
        }

        setUploading(false);

        if (remainingPending.length === 0) {
          setSubmitSuccess('Details and files saved successfully!');
        } else {
          setSubmitSuccess('Details saved, but some files failed to upload.');
        }
      } else {
        setSubmitSuccess('Details saved successfully!');
      }
      
      setLastSavedCore({
        title: form.title, type: form.type, folder: form.folder, visibility: form.visibility,
        releaseDate: form.releaseDate, released: form.released, timeLimitMinutes: form.timeLimitMinutes, preferPdf: form.preferPdf
      });

    } catch (err) {
      setUploading(false);
      setSubmitError(err.response?.data?.error || 'Failed to save details');
      return;
    }
  };

  const handleCancelCoreDetails = () => {
    setError('');
    setStatus('');
    setTitleError('');
    setFolderError('');
    setFileError('');
    setFileSuccess('');
    setSubmitError('');
    setSubmitSuccess('');
    setTimeLimitError('');

    if (!isEditing) {
      return handleBack();
    }

    if (isEditing || draftItemId) {
      setForm(prev => ({
        ...prev,
        ...lastSavedCore
      }));
    }

    setShowCoreDetails(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');
  };

  const renderPreviewToggle = (isInline = false) => (
    <button 
      type="button"
      onClick={() => setShowPreview(!showPreview)}
      className={`premium-icon-btn ${showPreview ? 'active' : ''}`}
      style={{ 
        background: showPreview ? '#10b981' : (isInline ? '#475569' : 'var(--bg-card)'), // Premium Emerald for active, slate for inline resting
        color: showPreview ? 'white' : (isInline ? 'rgba(255,255,255,0.9)' : 'var(--text-main)'),
        border: isInline ? 'none' : '1px solid var(--border-light)',
        width: '32px',
        height: '32px',
        borderRadius: isInline ? '50%' : '8px', // Match circle buttons when inline
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => { 
        if (isInline) {
          e.currentTarget.style.transform = 'scale(1.1)'; 
          e.currentTarget.style.boxShadow = 'var(--shadow-md)'; 
        }
      }}
      onMouseLeave={(e) => { 
        if (isInline) {
          e.currentTarget.style.transform = 'scale(1)'; 
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; 
        }
      }}
      title={showPreview ? "Hide Preview" : "Show Preview"}
    >
      <i className={`fas ${showPreview ? 'fa-eye' : 'fa-eye-slash'}`} style={{ fontSize: isInline ? '0.9rem' : 'inherit' }} />
    </button>
  );

  const renderHeaderToolbar = (fieldId, idx = null) => {
    const isQuestionField = fieldId === 'question-text-area' || fieldId.endsWith('-question-text-area');
    const targetQuestion = idx !== null ? editQuestionsData[idx] : question;
    const isDirty = targetQuestion && (
      targetQuestion.text.trim() !== '' || 
      targetQuestion.imageUrl ||
      targetQuestion.correctAnswers.length > 0 || 
      targetQuestion.options.some((opt, oIdx) => opt.trim() !== '' || targetQuestion.optionImages?.[oIdx])
    );
    const isEditingThis = idx !== null ? (idx in editQuestionsData) : (editingQuestionIndex !== null);
    const showSaveCancel = isQuestionField && (isDirty || isEditingThis || idx !== null);

    return (
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        alignItems: 'center'
      }}>
        {renderPreviewToggle(true)}
        <MathToolbar targetId={fieldId} />
        <SvgToolbar 
          targetId={fieldId} 
          onOpen={() => setIsSvgEditorOpen(true)}
          onClose={() => setIsSvgEditorOpen(false)}
        />
        {showSaveCancel && (
          <div style={{ display: 'flex', gap: '6px', marginLeft: '4px' }}>
            <button 
              type="button" 
              className="action-button" 
              onClick={() => saveQuestion(idx)} 
              style={{ 
                padding: '0 12px', 
                fontSize: '0.85rem', 
                borderRadius: '20px', 
                background: 'var(--success)', 
                borderColor: 'var(--success)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                height: '32px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-check" />
              Save
            </button>
            <button 
              type="button" 
              className="secondary" 
              onClick={async () => {
                const orig = idx !== null ? form.questions[idx] : null;
                await cleanupCancelledImages(targetQuestion, orig);
                if (idx !== null) {
                  setEditQuestionsData(prev => {
                    const next = { ...prev };
                    delete next[idx];
                    return next;
                  });
                } else {
                  setQuestion(emptyQuestion());
                }
                setError('');
              }} 
              style={{ 
                padding: '0 12px', 
                fontSize: '0.85rem', 
                borderRadius: '20px', 
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-times" />
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderInlineToolbar = (fieldId) => {
    if (fieldId === 'question-text-area' || fieldId.endsWith('-question-text-area')) return null; // Rendered in section/card headers
    if (lastFocusedId !== fieldId || showCoreDetails) return null;
    return (
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        alignItems: 'center',
        justifyContent: 'flex-end', 
        marginBottom: '6px',
        animation: 'fadeIn 0.2s ease-out'
      }}>
        {renderPreviewToggle(true)}
        <MathToolbar targetId={fieldId} />
        <SvgToolbar 
          targetId={fieldId} 
          onOpen={() => setIsSvgEditorOpen(true)}
          onClose={() => setIsSvgEditorOpen(false)}
        />
      </div>
    );
  };

  const isExam = form.type === 'exam_paper';

  const renderQuestionEditor = (currQuestion = question, setCurrQuestion = setQuestion, idx = null) => {
    const isNew = idx === null;
    const prefix = isNew ? 'new' : idx;
    const textAreaId = isNew ? 'question-text-area' : `q-${idx}-question-text-area`;
    const imageInputId = isNew ? 'question-image-input' : `q-${idx}-question-image-input`;

    return (
      <div style={{ display: 'grid', gap: '12px' }}>
        {error && (
          <div
            ref={questionErrorRef}
            className="error-message question-editor-error"
            role="alert"
            tabIndex="-1"
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {renderInlineToolbar(textAreaId)}
            <textarea
              id={textAreaId}
              value={currQuestion.text}
              onChange={e => setCurrQuestion(prev => ({ ...prev, text: e.target.value }))}
              onFocus={() => setLastFocusedId(textAreaId)}
              onPaste={(e) => handlePasteImage(e, 'question', idx)}
              placeholder="Enter question…"
              style={{ borderRadius: '12px', height: '100%', minHeight: '80px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
            {currQuestion.imageUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <img src={currQuestion.imageUrl} alt="Question Preview" style={{ height: '36px', width: '36px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-light)', cursor: 'zoom-in' }} onClick={() => window.open(currQuestion.imageUrl, '_blank')} />
                <button 
                  type="button" 
                  className="icon-button danger" 
                  onClick={() => handleQuestionImageDelete(idx)}
                  title="Remove Image"
                  style={{ minWidth: '32px', minHeight: '32px' }}
                >
                  <i className="fas fa-trash" />
                </button>
              </div>
            ) : (
              <div>
                <input 
                  type="file" 
                  accept="image/*" 
                  id={imageInputId} 
                  style={{ display: 'none' }} 
                  onChange={(e) => handleQuestionImageUpload(e, idx)} 
                />
                <button 
                  type="button" 
                  className="secondary" 
                  onClick={() => document.getElementById(imageInputId).click()}
                  disabled={uploadingQuestionImage}
                  style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '4px', height: '36px', borderRadius: '8px', cursor: 'pointer' }}
                  title="Upload Question Image"
                >
                  <i className="fas fa-image" />
                </button>
              </div>
            )}
          </div>
        </div>
        {showPreview && (currQuestion.text?.trim() || currQuestion.imageUrl) && (
          <div className="answer-preview-box">
            <div className="preview-label">Question Preview:</div>
            {currQuestion.text?.trim() && <MathText key={currQuestion.text} text={currQuestion.text} targetId={textAreaId} />}
            {currQuestion.imageUrl && (
              <div style={{ marginTop: '8px' }}>
                <img src={currQuestion.imageUrl} alt="Question Preview" style={{ maxHeight: '150px', borderRadius: '4px', border: '1px solid var(--border-light)' }} />
              </div>
            )}
          </div>
        )}

        <label>Question Type</label>
        <select
          value={currQuestion.type}
          onChange={e => setCurrQuestion(prev => ({ ...prev, type: e.target.value, correctAnswers: [] }))}
        >
          <option value="normal">Open / Short Answer</option>
          <option value="mcq">Multiple Choice (MCQ)</option>
        </select>

        {/* ── Marks row (MCQ only) ── */}
        {currQuestion.type === 'mcq' && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, display: 'grid', gap: '4px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 0 }}>
                <i className="fas fa-plus-circle" style={{ color: 'var(--success)', marginRight: '4px' }} />
                Correct (+ve marks)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.25"
                value={currQuestion.marks ?? 1}
                onChange={e => setCurrQuestion(prev => ({ ...prev, marks: parseFloat(e.target.value) || 0 }))}
                style={{ padding: '7px 10px', fontSize: '0.9rem', textAlign: 'center' }}
              />
            </div>
            <div style={{ flex: 1, display: 'grid', gap: '4px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 0 }}>
                <i className="fas fa-minus-circle" style={{ color: 'var(--danger)', marginRight: '4px' }} />
                Wrong (−ve marks)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.25"
                value={currQuestion.negativeMarks ?? 0}
                onChange={e => setCurrQuestion(prev => ({ ...prev, negativeMarks: parseFloat(e.target.value) || 0 }))}
                style={{ padding: '7px 10px', fontSize: '0.9rem', textAlign: 'center' }}
              />
            </div>
            <div style={{ flex: 2, fontSize: '0.78rem', color: 'var(--text-muted)', paddingBottom: '8px', lineHeight: 1.4 }}>
              Score per question: <strong style={{ color: 'var(--success)' }}>+{currQuestion.marks ?? 1}</strong> correct, <strong style={{ color: 'var(--danger)' }}>−{currQuestion.negativeMarks ?? 0}</strong> wrong
            </div>
          </div>
        )}

        {currQuestion.type === 'mcq' && (
          <div className="section">
            <label style={{ marginBottom: '8px', display: 'block' }}>
              Options — <span style={{ fontWeight: 400, color: '#6b7280' }}>check all correct answers</span>
            </label>

            {currQuestion.options.map((opt, optIdx) => {
              const optImg = currQuestion.optionImages?.[optIdx];
              const optIdentifier = opt?.trim() || optImg;
              const isCorrect = optIdentifier ? currQuestion.correctAnswers.includes(optIdentifier) : false;
              const optInputId = isNew ? `opt-input-${optIdx}` : `q-${idx}-opt-input-${optIdx}`;
              const optImageInputId = isNew ? `opt-image-input-${optIdx}` : `q-${idx}-opt-image-input-${optIdx}`;
              const correctCheckboxId = isNew ? `correct-${optIdx}` : `q-${idx}-correct-${optIdx}`;

              return (
                <div
                  key={optIdx}
                  className={`mcq-option-row${isCorrect ? ' mcq-option-correct' : ''}`}
                  onClick={(e) => {
                    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'BUTTON') {
                      const input = document.getElementById(optInputId);
                      if (input) input.focus();
                    }
                  }}
                  style={{ cursor: 'text' }}
                >
                  <input
                    type="checkbox"
                    id={correctCheckboxId}
                    checked={isCorrect}
                    onChange={() => toggleCorrectAnswer(optIdx, idx)}
                    className="mcq-checkbox"
                    title="Mark as correct answer"
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {renderInlineToolbar(optInputId)}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        id={optInputId}
                        type="text"
                        value={opt}
                        onChange={e => handleOptionChange(optIdx, e.target.value, idx)}
                        onFocus={() => setLastFocusedId(optInputId)}
                        onPaste={(e) => handlePasteImage(e, 'option', idx, optIdx)}
                        placeholder={`Option ${optIdx + 1}`}
                        style={{ flex: 1, border: '1px solid var(--border-light)', background: 'var(--bg-main)', padding: '8px 12px', outline: 'none', color: 'var(--text-main)', borderRadius: '8px' }}
                      />
                      {!currQuestion.optionImages?.[optIdx] && (
                        <div>
                          <input 
                            type="file" 
                            accept="image/*" 
                            id={optImageInputId} 
                            style={{ display: 'none' }} 
                            onChange={(e) => handleOptionImageUpload(optIdx, e, idx)} 
                          />
                          <button 
                            type="button" 
                            className="secondary" 
                            onClick={() => document.getElementById(optImageInputId).click()}
                            disabled={uploadingOptionImageIdx === optIdx}
                            style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '4px', height: '36px', borderRadius: '8px', cursor: 'pointer' }}
                            title="Upload Option Image"
                          >
                            <i className="fas fa-image" />
                          </button>
                        </div>
                      )}
                    </div>
                    {currQuestion.optionImages?.[optIdx] && (
                      <div style={{ position: 'relative', display: 'inline-block', marginTop: '6px' }}>
                        <img 
                          src={currQuestion.optionImages[optIdx]} 
                          alt={`Option ${optIdx+1}`} 
                          style={{ maxHeight: '150px', maxWidth: '100%', borderRadius: '6px', border: '1px solid var(--border-light)', cursor: 'zoom-in', display: 'block' }} 
                          onClick={() => window.open(currQuestion.optionImages[optIdx], '_blank')}
                        />
                        <button 
                          type="button" 
                          className="icon-button danger" 
                          onClick={() => handleOptionImageDelete(optIdx, idx)}
                          title="Remove Image"
                          style={{ position: 'absolute', top: '4px', right: '4px', minWidth: '28px', minHeight: '28px', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%' }}
                        >
                          <i className="fas fa-trash" style={{ fontSize: '0.7rem' }} />
                        </button>
                      </div>
                    )}
                    {showPreview && opt?.trim() && (
                      <div className="mcq-preview-inline">
                        <MathText key={opt} text={opt} targetId={optInputId} />
                      </div>
                    )}
                  </div>
                  {currQuestion.options.length > 2 && (
                    <button
                      type="button"
                      className="icon-button danger"
                      onClick={() => removeOption(optIdx, idx)}
                      title="Remove option"
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              );
            })}

            <button type="button" className="secondary" onClick={() => addOption(idx)} style={{ marginTop: '8px', width: 'auto' }}>
              + Add Option
            </button>

            {currQuestion.correctAnswers.length > 0 && (
              <div className="section" style={{ background: 'rgba(16, 185, 129, 0.1)', borderRadius: '10px', padding: '10px 14px', marginTop: '8px', border: '1px solid var(--success)' }}>
                <strong style={{ color: 'var(--success)', fontSize: '0.875rem' }}>✓ Correct answer(s):</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: '18px', color: 'var(--success)', fontSize: '0.875rem' }}>
                  {currQuestion.correctAnswers.map((a, i) => (
                    <li key={i}>
                      {isUrl(a) ? (
                        <img src={a} alt="Correct Choice" style={{ maxHeight: '50px', borderRadius: '4px', border: '1px solid var(--border-light)', display: 'block', marginTop: '4px' }} />
                      ) : (
                        <MathText key={a} text={a} />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {isNew && !(currQuestion.text.trim() !== '' || currQuestion.imageUrl || currQuestion.correctAnswers.length > 0 || currQuestion.options.some((opt, idx) => opt.trim() !== '' || currQuestion.optionImages?.[idx])) && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <button type="button" className="action-button" onClick={() => saveQuestion(null)} style={{ width: 'auto' }}>
              + Add Question
            </button>
          </div>
        )}
      </div>
    );
  };

  const serverItemId = isEditing ? existingItem?._id : draftItemId;

  return (
    <div style={{ padding: 0, position: 'relative', width: '100%' }}>
      <SEO title={isEditing ? `Edit: ${form.title}` : 'Create Content'} description={isEditing ? `Modify the details and questions for ${form.title}.` : 'Create new documents, textbooks, or exam papers for your students.'} />
      {/* Floating Back Button */}
      {!isSvgEditorOpen && (
        <button 
          className="secondary action-button" 
          type="button" 
          onClick={handleBack} 
          style={{ 
            position: 'fixed', 
            top: '24px', 
            left: '24px', 
            zIndex: 1000, 
            borderRadius: '50%', 
            width: '48px', 
            height: '48px', 
            padding: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: 'var(--shadow-lg)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-light)'
          }}
          title="Go Back"
        >
          <i className="fas fa-arrow-left" />
        </button>
      )}


      <div style={{ padding: '40px 32px 32px' }}>


        {pageError && <div className="error-message" style={{ marginBottom: '20px' }}>{pageError}</div>}

        <form onSubmit={handleSubmit}>
          {/* ── Core fields ── */}
          {!showCoreDetails ? (
            <div className="premium-card" style={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              gap: '16px', 
              marginBottom: '16px', 
              padding: '6px 20px', 
              cursor: 'default',
              margin: '-20px auto 24px',
              maxWidth: 'none',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}><MathText key={form.title} text={form.title} /></h3>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <span className="card-type-badge card-type-exam" style={{ margin: 0, fontSize: '0.6rem', padding: '1px 6px', height: 'auto' }}>{form.type === 'exam_paper' ? 'Exam' : 'Document'}</span>
                  <span className="status-badge" style={{ background: 'var(--bg-main)', color: 'var(--text-muted)', border: '1px solid var(--border-light)', fontSize: '0.6rem', padding: '1px 6px', height: 'auto' }}>{form.visibility}</span>
                </div>
              </div>
              <button
                type="button"
                className="premium-icon-btn"
                style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
                onClick={() => setShowCoreDetails(true)}
                title="Edit Details"
              >
                <i className="fas fa-pencil-alt" />
              </button>
            </div>
          ) : (
            <div className="premium-card" style={{ 
              display: 'grid', 
              gap: '16px', 
              position: 'relative', 
              marginBottom: '32px',
              margin: '-20px auto 32px',
              maxWidth: 'none',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ display: 'grid', gap: '8px' }}>
                <label>Title</label>
                <input name="title" value={form.title} onChange={handleChange} required placeholder="Enter title" />
                {titleError && <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: '2px' }}>{titleError}</div>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <label>Content Type</label>
                  <select name="type" value={form.type} onChange={handleChange}>
                    <option value="document">Document</option>
                    <option value="textbook">Textbook</option>
                    <option value="exam_paper">Exam Paper</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <label>Folder</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" className="secondary" onClick={(e) => { e.preventDefault(); setShowPicker(true); }} style={{ flex: 1, textAlign: 'left', background: 'var(--bg-card)', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-light)' }}>
                      {form.folder ? (folders.find(f => f._id === form.folder)?.title || 'Select folder...') : 'Root Folder'}
                    </button>
                    {form.folder && (
                      <button type="button" className="premium-icon-btn danger" onClick={(e) => { e.preventDefault(); setForm(prev => ({ ...prev, folder: '' })); }} title="Clear selection">
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>
                  {folderError && <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: '2px' }}>{folderError}</div>}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <label>Visibility</label>
                  <select name="visibility" value={form.visibility} onChange={handleChange}>
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <label>Release Date</label>
                  <input type="date" name="releaseDate" value={form.releaseDate} onChange={handleChange} required />
                </div>
              </div>

              {/* PDF Upload */}
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'grid', gap: '8px', border: '1.5px dashed var(--border-light)', padding: '20px', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
                  <label>{isExam ? 'Exam Papers (PDF)' : 'Documents (PDF)'}</label>
                  
                  {form.files && form.files.length > 0 && (
                    <div style={{ display: 'grid', gap: '8px', textAlign: 'left', marginBottom: '16px' }}>
                      {form.files.map((fileObj, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
                            <i className="fas fa-file-pdf" style={{ color: 'var(--danger)', fontSize: '1.2rem' }} />
                            <span style={{ fontWeight: 500, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {fileObj.name || `File ${idx + 1}`}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <a
                              href="#"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const res = await api.post('/files/presign-url', { url: fileObj.url });
                                  window.open(res.data.presignedUrl, '_blank');
                                } catch (err) {
                                  window.open(fileObj.url, '_blank');
                                }
                              }}
                              className="premium-icon-btn"
                              title="Preview file"
                              style={{ color: 'var(--text-main)', textDecoration: 'none' }}
                            >
                              <i className="fas fa-external-link-alt" />
                            </a>
                            <button
                              type="button"
                              className="premium-icon-btn"
                              onClick={() => toggleFileVisibility(idx)}
                              title={fileObj.visibility === 'private' ? 'Make public' : 'Make private'}
                              style={{ color: fileObj.visibility === 'private' ? 'var(--text-muted)' : 'var(--primary)', marginRight: '4px' }}
                            >
                              <i className={`fas ${fileObj.visibility === 'private' ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </button>
                            <button 
                              type="button" 
                              className="premium-icon-btn"  
                              onClick={() => {
                                setDeleteConfirmIdx(idx);
                                setShowDeleteFileConfirm(true);
                              }}
                              disabled={deleting}
                              title="Remove and delete file"
                              style={{ color: 'var(--danger)' }}
                            >
                              <i className={`fas ${deleting && deleteConfirmIdx === idx ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {pendingFiles.length > 0 && (
                    <div style={{ display: 'grid', gap: '8px', marginBottom: '16px', textAlign: 'left' }}>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Files pending upload (click Save Basic Info to upload):</strong>
                      {pendingFiles.map((f, idx) => (
                        <div key={`pending-${idx}`} className="premium-card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-main)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className="fas fa-file-pdf" style={{ color: 'var(--danger)', fontSize: '1.2rem' }} />
                            <div>
                              <strong style={{ display: 'block', fontSize: '0.95rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</strong>
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{(f.size / 1024 / 1024).toFixed(2)} MB • Ready to upload</span>
                            </div>
                          </div>
                          <button 
                            type="button" 
                            className="premium-icon-btn" 
                            onClick={() => removePendingFile(idx)}
                            disabled={uploading}
                            title="Remove from queue"
                            style={{ color: 'var(--danger)' }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input 
                    type="file" 
                    accept="application/pdf" 
                    multiple
                    onChange={handleFileSelect} 
                    style={{ display: 'none' }} 
                    id="pdf-upload-input" 
                  />
                  <button 
                    type="button" 
                    className="secondary" 
                    disabled={uploading}
                    onClick={() => document.getElementById('pdf-upload-input').click()}
                  >
                    {uploading ? (
                      <><i className="fas fa-spinner fa-spin" /> Uploading...</>
                    ) : (
                      <><i className="fas fa-cloud-upload-alt" /> {form.files && form.files.length > 0 ? 'Upload More PDFs' : 'Upload PDF'}</>
                    )}
                  </button>
                  <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>You can upload multiple PDF files (up to 50MB each)</p>
                </div>
                {fileError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: '4px' }}>{fileError}</div>}
                {fileSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginTop: '4px' }}>{fileSuccess}</div>}
              </div>

              {/* Time limit — exam only */}
              {form.type === 'exam_paper' && (
                <div style={{ display: 'grid', gap: '8px' }}>
                  <label>Time Limit (Minutes)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="number"
                      name="timeLimitMinutes"
                      value={form.timeLimitMinutes}
                      onChange={handleChange}
                      min="1"
                      max="360"
                      placeholder="e.g. 60"
                      style={{ width: '120px' }}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Leave blank for no limit</span>
                  </div>
                  {timeLimitError && <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: '2px' }}>{timeLimitError}</div>}
                </div>
              )}

              <label className="released-toggle" style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none', background: 'var(--bg-main)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                <input
                  type="checkbox"
                  checked={!!form.released}
                  onChange={(e) => setForm(prev => ({ ...prev, released: e.target.checked }))}
                  style={{ width: '20px', height: '20px', accentColor: 'var(--success)', cursor: 'pointer' }}
                />
                <div>
                  <strong style={{ color: form.released ? 'var(--success)' : 'var(--text-muted)', fontSize: '0.95rem' }}>
                    {form.released ? '✓ Released to Browse' : 'Not Released (Draft)'}
                  </strong>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {form.released ? 'Students can find this paper in Browse' : 'Only you and authorized editors can see this'}
                  </span>
                </div>
              </label>

              {form.type === 'exam_paper' && form.fileUrl && (
                <label className="released-toggle" style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none', background: 'var(--bg-main)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!form.preferPdf}
                    onChange={(e) => setForm(prev => ({ ...prev, preferPdf: e.target.checked }))}
                    style={{ width: '20px', height: '20px', accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                  <div>
                    <strong style={{ color: form.preferPdf ? 'var(--primary)' : 'var(--text-muted)', fontSize: '0.95rem' }}>
                      {form.preferPdf ? 'Show PDF Only' : 'Use Interactive Exam (Default)'}
                    </strong>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {form.preferPdf ? 'Students will view the PDF directly instead of answering questions' : 'Students will take the interactive test even if a PDF is attached'}
                    </span>
                  </div>
                </label>
              )}

              {submitError && <div className="error-message" style={{ marginBottom: '12px' }}>{submitError}</div>}
              {submitSuccess && <div className="status-message" style={{ marginBottom: '12px' }}>{submitSuccess}</div>}
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button type="button" onClick={saveCoreDetails} style={{ width: 'auto' }} disabled={uploading}>
                  {uploading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />} Save Basic Info
                </button>
                <button 
                  type="button" 
                  className="secondary" 
                  onClick={(!hasUnsavedChanges && (isEditing || draftItemId)) ? () => setShowCoreDetails(false) : handleCancelCoreDetails} 
                  style={{ width: 'auto' }}
                  disabled={uploading}
                >
                  {(!hasUnsavedChanges && (isEditing || draftItemId)) ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          )}

          {/* ── Question builder (exam only) ── */}
          {isExam && (
            <div className="section" id="add-question-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Add Question</h3>
                {renderHeaderToolbar('question-text-area')}
              </div>
              {renderQuestionEditor(question, setQuestion, null)}
            </div>
          )}

          {/* ── Question list ── */}
          {isExam && form.questions.length > 0 && (
            <div style={{ marginTop: '40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h3 style={{ margin: 0 }}>Questions ({form.questions.length})</h3>
                 <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}><i className="fas fa-list-ol" style={{ marginRight: '6px' }} />Enter number to reorder</span>
              </div>
              {form.questions.map((q, idx) => (
                <div key={q._id || q.keyId} className="question-card" id={`question-card-${idx}`}>
                  {idx in editQuestionsData ? (
                    <>
                       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span className="question-number">{idx + 1}</span>
                          <h4 style={{ color: 'var(--primary)', margin: 0 }}>Editing Question</h4>
                        </div>
                        {renderHeaderToolbar(`q-${idx}-question-text-area`, idx)}
                      </div>
                      {renderQuestionEditor(editQuestionsData[idx], (updater) => updateEditQuestionData(idx, updater), idx)}
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', alignSelf: 'flex-start', marginRight: '8px' }}>
                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>No.</span>
                            <QuestionNumberInput
                              idx={idx}
                              totalQuestions={form.questions.length}
                              onMove={handleMoveQuestion}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                              <div className="card-type-badge card-type-doc" style={{ fontSize: '0.65rem' }}>{q.type === 'mcq' ? 'Multiple Choice' : 'Open Answer'}</div>
                              {q.type === 'mcq' && (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: 'rgba(16,185,129,0.12)', color: 'var(--success)' }}>
                                    +{q.marks ?? 1}
                                  </span>
                                  {(q.negativeMarks ?? 0) > 0 && (
                                    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: 'rgba(239,68,68,0.12)', color: 'var(--danger)' }}>
                                      −{q.negativeMarks}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                              <div style={{ fontSize: '1.05rem', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                <MathText key={q.text} text={q.text} />
                              </div>
                              {q.imageUrl && (
                                <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
                                  <img src={q.imageUrl} alt="Question Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid var(--border-light)', cursor: 'zoom-in' }} onClick={() => window.open(q.imageUrl, '_blank')} />
                                </div>
                              )}
                              
                              {q.type === 'mcq' && (
                                <div style={{ marginTop: '16px', display: 'grid', gap: '8px' }}>
                                  {q.options.map((o, i) => {
                                    const optImg = q.optionImages?.[i];
                                    const optIdentifier = o?.trim() || optImg;
                                    if (!optIdentifier) return null;
                                    const isCorrect = q.correctAnswers.includes(optIdentifier);
                                    return (
                                      <div key={i} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '10px', 
                                        padding: '8px 12px', 
                                        borderRadius: '8px',
                                        background: isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-card)',
                                        border: '1px solid',
                                        borderColor: isCorrect ? 'var(--success)' : 'var(--border-light)'
                                      }}>
                                        <div style={{ 
                                          width: '18px', 
                                          height: '18px', 
                                          borderRadius: '50%', 
                                          border: '2px solid',
                                          borderColor: isCorrect ? 'var(--success)' : 'var(--border-light)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'var(--success)',
                                          fontSize: '0.7rem'
                                        }}>
                                          {isCorrect && <i className="fas fa-check" />}
                                        </div>
                                        <div style={{ flex: 1, fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                          {o?.trim() ? <MathText key={o} text={o} /> : null}
                                          {optImg && (
                                            <div style={{ marginTop: '4px' }}>
                                              <img src={optImg} alt={`Option ${i+1}`} style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '6px', border: '1px solid var(--border-light)', cursor: 'zoom-in' }} onClick={() => window.open(optImg, '_blank')} />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {serverItemId && q._id && (
                            <a
                              href={`/question/${serverItemId}/${q._id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="premium-icon-btn"
                              title="Open question in a new page"
                              style={{ textDecoration: 'none' }}
                            >
                              <i className="fas fa-external-link-alt" />
                            </a>
                          )}
                          <button
                            type="button"
                            className="premium-icon-btn"
                            onClick={() => editQuestion(idx)}
                            title="Edit question"
                          >
                            <i className="fas fa-pencil-alt" />
                          </button>
                          <button
                            type="button"
                            className="premium-icon-btn danger"
                            onClick={() => setDeleteConfirmIdx(idx)}
                            title="Remove question"
                            style={{ color: 'var(--danger)' }}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

        </form>
      </div>
        <Modal isOpen={deleteConfirmIdx !== null && !showDeleteFileConfirm} onClose={() => setDeleteConfirmIdx(null)} title="Delete Question?" style={{ maxWidth: '400px', textAlign: 'center' }}>
          <p style={{ margin: '16px 0', color: 'var(--text-muted)' }}>Are you sure you want to delete this question? This action cannot be undone.</p>
          <div className="modal-actions" style={{ justifyContent: 'center' }}>
            <button type="button" className="secondary" onClick={() => setDeleteConfirmIdx(null)}>No, Keep It</button>
            <button type="button" onClick={async () => { const idx = deleteConfirmIdx; setDeleteConfirmIdx(null); await removeQuestion(idx); }} style={{ background: 'var(--danger)' }}>Yes, Delete</button>
          </div>
        </Modal>

        <Modal isOpen={showDeleteFileConfirm} onClose={() => { setShowDeleteFileConfirm(false); setDeleteConfirmIdx(null); }} title="Delete PDF?" style={{ maxWidth: '400px', textAlign: 'center' }}>
          <p style={{ margin: '16px 0', color: 'var(--text-muted)' }}>Are you sure you want to permanently delete this PDF file from the server?</p>
          <div className="modal-actions" style={{ justifyContent: 'center' }}>
            <button type="button" className="secondary" onClick={() => { setShowDeleteFileConfirm(false); setDeleteConfirmIdx(null); }}>No, Keep File</button>
            <button type="button" onClick={async () => { setShowDeleteFileConfirm(false); await handleFileDelete(); }} style={{ background: 'var(--danger)' }}>Yes, Delete</button>
          </div>
        </Modal>

        {isExam && (
          <>
            {/* Floating Navigation Button */}
            <button 
              className={`floating-map-btn ${showMap ? 'active' : ''}`}
              onClick={() => setShowMap(!showMap)}
              title="Question Map"
              type="button"
            >
              <i className="fas fa-th-large" />
            </button>

            {showMap && (
              <div 
                className="test-nav-panel" 
                style={{ 
                  ...panelStyle, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden',
                  maxHeight: 'calc(100vh - 200px)' 
                }}
              >
                {isMobileOrTablet && (
                  <div 
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                    style={{
                      width: '100%',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'ns-resize',
                      backgroundColor: 'transparent',
                      marginTop: '-12px',
                      marginBottom: '8px',
                      touchAction: 'none'
                    }}
                    className="panel-drag-handle"
                  >
                    <div style={{
                      width: '40px',
                      height: '4px',
                      borderRadius: '2px',
                      backgroundColor: 'var(--border-light)',
                    }} />
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    Questions Map ({form.questions.length})
                  </span>
                  <button 
                    onClick={() => setShowMap(false)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', margin: '0', marginRight: '-12px', width: '18px', height: '18px', minWidth: '0', minHeight: '0', lineHeight: '1' }}
                    title="Close"
                    type="button"
                  >
                    <i className="fas fa-times" style={{ fontSize: '0.75rem', lineHeight: '1' }} />
                  </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginTop: '8px' }}>
                  <div className="question-grid">
                    {form.questions.map((_, idx) => {
                      const isCurrent = idx in editQuestionsData;
                      const dotClass = isCurrent ? 'dot-current' : 'dot-done';
                      
                      return (
                        <button
                          key={idx}
                          type="button"
                          className={`question-dot ${dotClass}`}
                          onClick={() => {
                            document.getElementById(`question-card-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            if (isMobileOrTablet) {
                              setShowMap(false);
                            }
                          }}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                    
                    {/* Add Question Button/Dot */}
                    <button
                      type="button"
                      className="question-dot dot-current"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => {
                        document.getElementById('add-question-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        if (isMobileOrTablet) {
                          setShowMap(false);
                        }
                      }}
                      title="Add Question"
                    >
                      <i className="fas fa-plus" style={{ fontSize: '0.8rem' }} />
                    </button>
                  </div>
                  
                  <div className="nav-legend" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-light)', fontSize: '0.75rem' }}>
                    <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--primary)' }} />
                      <span>Editing</span>
                    </div>
                    <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10b981', opacity: 0.4 }} />
                      <span>Saved</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      {showPicker && (
        <FolderPickerModal
          title="Select Folder"
          folders={folders}
          allowRoot={true}
          initialFolderId={form.folder}
          onSelect={(id) => { setForm(prev => ({ ...prev, folder: id })); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

export default ContentFormPage;
